import React, { useReducer, useState, useEffect, useRef } from "react";
import c from "classnames";
import PropTypes from "prop-types";
import { fasIcon, farIcon } from "h";
import { xhrGet, xhrPost } from "common/xhr";
import { buildQueryString } from "common/util";
import styles from "./realnames_results.module.sass";

function AddToCartButton(props) {
  const [isHover, setIsHover] = useState(false);
  const text = props.inCart ? "Remove from cart" : "Add to cart";
  return (
    <button
      className={c("btn", "submit", "primary", "add-to-cart")}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={() => props.clickHandler()}
    >
      <CartButtonIcon
        loading={props.isLoading}
        inCart={props.inCart}
        isHover={isHover}
      />
      <span>{text}</span>
    </button>
  );
}

function CartButtonIcon(props) {
  if (props.loading) {
    return fasIcon("spinner.fa-spin");
  }

  if (props.inCart) {
    return props.isHover ? fasIcon("times") : fasIcon("check");
  } else {
    return fasIcon("plus");
  }
}

function addToCart(name, recurring_plan_id, dispatch) {
  xhrPost("/cart/add_to_cart", {
    recurring_plan_id,
    name
  }).then(data => dispatch({
    type: "update_cart",
    cart: data.cart
  })).finally(() => dispatch({
    type: "update_loading",
    name,
    loading: false
  }))
}

function removeFromCart(name, dispatch) {
  xhrPost("/cart/remove_cart", {
    name
  }).then(data => dispatch({
    type: "update_cart",
    cart: data.cart
  })).finally(() => dispatch({
    type: "update_loading",
    name,
    loading: false
  }))
}

function Result(props) {
  const clickHandler = () => {
    if (props.loading) {
      return;
    }
    props.dispatch({
      type: "update_loading",
      name: props.result.address,
      loading: true
    })
    if (props.inCart) {
      removeFromCart(props.result.address, props.dispatch)
    } else {
      addToCart(props.result.address, props.plan.plan_id, props.dispatch)
    }
  };
  return (
    <div className={styles.result}>
      <div>
        <p data-testid={`${props.result.address}-result`}><span className={styles.address}>{props.result.address}</span> is available!</p>
        <p>This service renews yearly</p>
      </div>
      <div className={styles.cart}>
        <p className={styles.price}>{props.plan.formatted_price}</p>
        <AddToCartButton inCart={props.inCart} isLoading={props.isLoading} clickHandler={clickHandler}/>
      </div>
    </div>
  );
}

function updateCartButton(count) {
  const navCart = document.querySelector(".nav__cart");
  navCart.style.display = count > 0 ?  "" : "none";
  navCart.querySelector(".cart__count").innerHTML = count !== 1 ? `${count} Items` : "1 Item"
}

function isInCart(cart, name) {
  return !!cart.find(item => item.name === name)
}

function reducer(state, action) {
  switch (action.type) {
    case "update_cart":
      // This is less than ideal, but the cart button is not
      // controlled with react so we have to manually update it
      updateCartButton(action.cart.length)
      return { ...state, cart: action.cart };

    case "update_loading":
      return {
        ...state,
        loading: { ...state.loading, [action.name]: action.loading },
      };

    case "update_remaining":
      return {
        ...state,
        results: {
          ...state.results,
          similar: [
            ...state.results.similar,
            ...action.mailboxes
          ]
        }
      }

    case "set_remainder_loading":
      return {
        ...state,
        remainderLoading: action.remainderLoading
      }

    case "set_show_progress_bar":
      return {
        ...state,
        showProgressBar: action.showProgressBar
      }

    default:
      return state;
  }
}

const touts = [
  {
    icon: "user",
    header: "Own your brand",
    copy: "You’ll own your name and personal brand online, and have control over its presentation"
  },
  {
    icon: "award",
    header: "Keep it yours",
    copy: "Professionalism and reliability go hand in hand with personally-owned online properties"
  },
  {
    icon: "shield-check",
    header: "Lock it down",
    copy: "Get ahead and secure name now rather than having to purchase it from someone else at a much higher cost"
  }
];

function ToutSection(props) {
  return <div className={styles.touts}>
    <p className={styles.feature}>Here are some of the features you get with a Realnames email</p>
    <div className={styles.tout_holder}>
      {touts.map((tout) => <Tout key={tout.icon} icon={tout.icon} header={tout.header} copy={tout.copy}/>)}
    </div>
  </div>;
}

function Tout(props) {
  return <div className={styles.tout}>
    <Icon icon={props.icon} />
    <div className={styles.text}>
      <p className={styles.header}>{props.header}</p>
      <p className={styles.copy}>{props.copy}</p>
    </div>
  </div>
}

function Icon(props) {
  return <div className={styles.icon}>
    { farIcon(props.icon) }
    <div className={styles.parallelogram} />
  </div>
}

function Results(props) {
  if (props.results.length === 0) {
    return null;
  }
  return <div data-testid={`${props.type}-results`}>
    <h2 className={styles.results_header}>{props.header}</h2>
    { props.children }
    {
      props.results.map((result) => (
        <Result
          key={result.address}
          result={result}
          isLoading={props.loading[result.address]}
          inCart={isInCart(props.cart, result.address)}
          dispatch={props.dispatch}
          plan={props.plan}
        />
      ))
    }
  </div>
}

function ExactResults(props) {
  const header = "Here's what we have for your name";
  return <Results {...props} header={header} type="exact"/>;
}

function SimilarResults(props) {
  const header = "Similar names";
  return <Results {...props} header={header} type="similar"/>;
}

function ProgressBar(props) {
  const idRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!props.remainderLoading) {
      setWidth(100)
      if (idRef.current) {
        clearTimeout(idRef.current);
      }
    } else {
      const percentage = (1 / props.remainder.length) * 100;
      if (width < 100 - percentage) {
        idRef.current = setTimeout(() => {
          setWidth(width + percentage);
        }, 300)
      }
    }
  }, [width, props.remainderLoading]);

  const innerStyle = {
    width: `${width}%`
  }

  return (
    <div className={styles.progress_bar_holder}>
      <div className={styles.progress_bar_inner} style={innerStyle}/>
    </div>
  )
}

async function loadRemainder(props, dispatch) {
  if (props.remainder.length == 0) {
    return
  }
  dispatch({ type: "set_remainder_loading", remainderLoading: true });
  dispatch({ type: "set_show_progress_bar", showProgressBar: true });
  const { first_name, last_name } = props;
  const queryString = buildQueryString({
    first_name,
    last_name,
    domain_names: props.remainder.join(",")
  });
  const res = await xhrGet(`/email/realnames/remaining_results${queryString}`);
  dispatch({ type: "set_remainder_loading", remainderLoading: false });
  if (res.succeeded) {
    dispatch({ type: "update_remaining", mailboxes: res.mailboxes });
  }
  setTimeout(
    () => dispatch({ type: "set_show_progress_bar", showProgressBar: false }),
    1000
  );
}

function RealnamesResults(props) {
  const [state, dispatch] = useReducer(reducer, {
    cart: props.cart,
    results: props.results,
    loading: {},
    showProgressBar: false,
    remainderLoading: false
  });
  useEffect(() => {
    loadRemainder(props, dispatch)
  }, []);
  return (
    <div className={styles.results_page}>
      <div className={styles.results}>
        <ExactResults
          results={state.results.exact}
          loading={state.loading}
          cart={state.cart}
          dispatch={dispatch}
          plan={props.plan}
        />
        <SimilarResults
          results={state.results.similar}
          loading={state.loading}
          cart={state.cart}
          dispatch={dispatch}
          plan={props.plan}
        >
          {state.showProgressBar && <ProgressBar remainder={props.remainder} remainderLoading={state.remainderLoading}/>}
        </SimilarResults>
      </div>
      <ToutSection />
    </div>
  );
}

const resultShape = PropTypes.shape({
  address: PropTypes.string.isRequired
})

RealnamesResults.propTypes = {
  cart: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired
  })),
  results: PropTypes.shape({
    exact: PropTypes.arrayOf(resultShape),
    similar: PropTypes.arrayOf(resultShape)
  }).isRequired
}

export default RealnamesResults;
